"use client";

import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { mutationOptions, useMutation } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { queueManagementApi } from "../api";
import type { Queue, UpdateQueueInput } from "../types";

interface EditQueueDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	queue: Queue;
}

const editQueueSchema = z.object({
	name: z
		.string()
		.min(1, "Queue name is required")
		.max(100, "Queue name must not exceed 100 characters"),
	voidPrintsEnabled: z.boolean(),
});

type EditQueueFormValues = z.infer<typeof editQueueSchema>;

export function EditQueueDialog({
	open,
	onOpenChange,
	tenant,
	queue,
}: EditQueueDialogProps) {
	const mutation = useMutation(
		mutationOptions({
			...queueManagementApi.updateQueueOptions(tenant, queue.id),
			onSuccess: (result, _variables, _onMutateResult, context) => {
				if (result.success) {
					toast.success("Queue updated successfully.");
					context.client.invalidateQueries({
						queryKey: [tenant, "queues"],
					});
					handleClose(false);
				} else {
					const errorDetails = result.details as
						{ detail?: string } | undefined;
					toast.error(
						errorDetails?.detail || "Failed to update queue. Please try again.",
					);
				}
			},
			onError: () => {
				toast.error("Unable to update queue. Please try again.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			name: queue.name,
			voidPrintsEnabled: queue.voidPrintsEnabled,
		} as EditQueueFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: editQueueSchema,
		},
		onSubmit: async ({ value }) => {
			const input: UpdateQueueInput = {};

			if (value.name !== queue.name) {
				input.name = value.name.trim();
			}

			if (value.voidPrintsEnabled !== queue.voidPrintsEnabled) {
				input.voidPrintsEnabled = value.voidPrintsEnabled;
			}

			if (Object.keys(input).length === 0) {
				handleClose(false);
				return;
			}

			await mutation.mutateAsync(input);
		},
	});

	React.useEffect(() => {
		if (open) {
			form.reset({
				name: queue.name,
				voidPrintsEnabled: queue.voidPrintsEnabled,
			});
		}
	}, [queue, open, form]);

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit Queue</DialogTitle>
					<DialogDescription>
						Update the queue&apos;s name or void prints. Its printers are on the
						queue&apos;s page.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to update queue. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Queue Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Kitchen, Bar, Pizza Oven"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="voidPrintsEnabled">
						{(field) => (
							<field.FormItem>
								<div className="flex items-center space-x-2">
									<Checkbox
										id="edit-voidPrintsEnabled"
										checked={field.state.value}
										onCheckedChange={(checked) =>
											field.handleChange(checked === true)
										}
									/>
									<Label htmlFor="edit-voidPrintsEnabled" className="text-sm">
										Enable void prints
									</Label>
								</div>
								<field.FormDescription>
									When enabled, removing a sent item will print a void ticket to
									this queue&apos;s printers.
								</field.FormDescription>
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Save Changes
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
